/* UMAI shared cart read — one round trip instead of a dozen.
 *
 * MEASURED, on one product-page load: nine separate `GET /cart.js` and three
 * `/cart.json`. The cart drawer, the PDP's buy column, the exclusive gate, the
 * "you may also like" rail, the pack picker and the upsell each fetch the cart
 * for themselves at boot, and each of them ALSO refetches on `cart:updated` —
 * so a single add-to-bag fanned out into another handful of identical requests.
 * On a phone on a slow connection that is a dozen sequential round trips for
 * one piece of state.
 *
 * WHY THIS IS A FETCH SHIM AND NOT A SHARED MODULE. The obvious fix is a
 * `UMAI_CART.get()` that every caller uses — and it means editing nine call
 * sites, each with its own idea of when it needs fresh data, in files owned by
 * different parts of the theme. This coalesces at the transport instead:
 * callers keep their code and their timing exactly as written, and simply share
 * one response.
 *
 * CORRECTNESS, which matters more than the saving:
 *   - only GET /cart.js and /cart.json are touched. Nothing else, ever.
 *   - concurrent reads share the ONE in-flight request; each caller gets its
 *     own `Response` clone, so every one of them can still read the body.
 *   - a resolved result is reused for TTL milliseconds and no longer. TTL is
 *     deliberately shorter than any human interaction — it exists to collapse
 *     the boot-time herd and the cascade after a mutation, not to hold state.
 *   - ANY cart mutation (/cart/add, /change, /update, /clear) drops the cache
 *     the moment it is issued, so nothing can read a pre-mutation cart after
 *     one has been sent. Same for XHR, which some apps still use.
 *   - a failed read is never cached.
 *
 * Worst case if this file does not load: every caller fetches its own cart,
 * exactly as before. Nothing depends on it being present.
 */
(function () {
  'use strict';
  if (window.__umaiCartShim) return;
  window.__umaiCartShim = true;

  var TTL = 300;
  var inflight = null, cachedAt = 0, cached = null;

  var READ = /\/cart\.(js|json)(\?|$)/;
  var WRITE = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;

  function drop() { inflight = null; cached = null; cachedAt = 0; }

  /* A mutation anywhere invalidates, including ones this shim did not make. */
  document.addEventListener('cart:updated', drop);
  document.addEventListener('cart:refresh', drop);

  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      var url = '';
      try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (e) { url = ''; }
      var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();

      if (WRITE.test(url)) { drop(); return nativeFetch.apply(this, arguments); }
      if (method !== 'GET' || !READ.test(url)) return nativeFetch.apply(this, arguments);

      var now = Date.now();
      if (cached && (now - cachedAt) < TTL) return Promise.resolve(cached.clone());
      if (inflight) return inflight.then(function (r) { return r.clone(); });

      var args = arguments, self = this;
      inflight = nativeFetch.apply(self, args).then(function (r) {
        /* Only a good response is worth sharing; an error must not be pinned
           in front of every other caller for the next 300ms. */
        if (r && r.ok) { cached = r.clone(); cachedAt = Date.now(); }
        inflight = null;
        return r;
      }).catch(function (e) { drop(); throw e; });

      return inflight.then(function (r) { return r.clone(); });
    };
  }

  /* Some installed apps still write the cart over XMLHttpRequest. They will not
     benefit from the coalescing, but they MUST still invalidate it — otherwise
     the theme could read a cart from before their change. */
  var open = window.XMLHttpRequest && window.XMLHttpRequest.prototype.open;
  if (open) {
    window.XMLHttpRequest.prototype.open = function (m, u) {
      try { if (WRITE.test(String(u || ''))) drop(); } catch (e) {}
      return open.apply(this, arguments);
    };
  }
})();
